import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { getUserRegionViaOpenCage } from "../../services/callAPIExtern/withOpenCageData";
import { SpinnerC } from "../../components/spinners/SpinnerC";
import SearchTrocsTable from "../../components/tables/SearchTrocsTable";
import { getResidentsTrocsList } from "../../services/callAPI/toResidentsTrocs";
import { ErrorOccurred } from "../../services/utils/ErrorOccurred";
import { ISearchTrocResidentExtended } from "../../services/interfaces/ITrocResident2";
import { getTravelersTrocsList } from "../../services/callAPI/toTravelerTrocs";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { Geolocation } from '@capacitor/geolocation';
import GpsToast from "../../components/gpsToast/GpsToast";
import { App } from '@capacitor/app';

function SearchTrocs() {
  const [loading, setLoading] = useState<boolean | null>(false);

  const [gpsLoadingError, setgpsLoadingError] = useState<boolean | null>(null);

  const [region, setRegion] = useState<string | null>(null);

  const [rTrocsList, setRTrocsList] = useState<ISearchTrocResidentExtended[]>([]);

  const [travTrocsList, settravTrocsList] = useState<null | []>(null);

  const [isShowNoPartnerFound, setIsShowNoPartnerFound] = useState<boolean>(false);

  const [triggerUseEffect, settriggerUseEffect] = useState<boolean>(false);

  const [showToastGpsSetting, setShowToastGpsSetting] = useState(false);

  const isWaitingForLocationActivation = useRef(false);

  //get user role via redux
  const userRole = useSelector((state: RootState) => state.user.role);

  //get user location
  async function recoverGpsCoord() {
    try {
      const plateform = Capacitor.getPlatform();
      let gpsCoord: { coords: { latitude: number, longitude: number }, timestamp: number } | undefined;

      switch (plateform) {
        case "web":
          gpsCoord = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              maximumAge: 1000,
              timeout: 60000,
            });
          });
          break;

        case "android": {
          const currentPermissions = await Geolocation.checkPermissions();

          if (currentPermissions.location !== 'granted') {
            const permStatus = await Geolocation.requestPermissions();
            if (permStatus.location !== 'granted') {
              alert("Vous devez autoriser la localisation pour utiliser cette fonctionnalité.");
              throw new Error("Permission GPS refusée");
            }
          }

          gpsCoord = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 60000,
            maximumAge: 1000,
          });
          break;
        }

        case "ios":

        default:
          throw new Error('Plateforme inconnue');
      }

      if (!gpsCoord) throw new Error("Position non récupérée");

      return gpsCoord;
    }
    catch (error) {
      // Ici, on suppose que le GPS est désactivé
      setShowToastGpsSetting(true);
      throw new Error("On n'a pû récupérer vos coord gps");
    }
  }

  //get user region
  async function recoverRegionTrav(gpsCoord: { coords: { latitude: number, longitude: number }, timestamp: number }) {
    try {
      //call API to get region via openCage
      const userRegion = await getUserRegionViaOpenCage(gpsCoord.coords.latitude.toString(), gpsCoord.coords.longitude.toString());

      return userRegion;

    } catch (error) {

      throw new Error("On n'a pû trouver votre position");
    }
  }

  const handleConfirmOpenSettings = async () => {
    setShowToastGpsSetting(false);

    isWaitingForLocationActivation.current = true;
    const win = window as any;

    if (
      win.cordova &&
      win.cordova.plugins &&
      win.cordova.plugins.settings &&
      typeof win.cordova.plugins.settings.open === 'function'
    ) {
      win.cordova.plugins.settings.open('location',
        () => console.log('Paramètres ouverts'),
        () => console.log('Échec de l’ouverture des paramètres')
      );
    } else {
      //console.log('openNativeSettingsTest is not active!');
    }
  };

  useEffect(() => {
    const getTrocsUsers = async () => {
      try {
        setLoading(true); //start spinner

        switch (userRole) {
          case "TRAVELER":
            //get data gps via API JS
            const gpsCoord = await recoverGpsCoord();

            const userRegion = await recoverRegionTrav(gpsCoord);

            //call API
            const resTrocs = await getResidentsTrocsList({ region: userRegion });

            if (resTrocs.data.message === "On n'a pas touvé de trocs dans cette region" || resTrocs.data.message === "On n'a pas touvé de trocs dans cette region pour le moment") {
              setgpsLoadingError(null);//no show gspLoadingError

              //update RTrocList to empty
              setRTrocsList([]);

              //show NoPartnerFound component
              setIsShowNoPartnerFound(true);
            }
            else if (resTrocs.data.message === "On a touvé des trocs dans cette region") {
              setRTrocsList(resTrocs.data.residentsTrocs);

              setRegion(userRegion);
            }
            else {
              throw new Error("On ne connait pas le mesage de la reponse");
            }

            break;
          case "RESIDENT":
            //call API
            const travTrocs = await getTravelersTrocsList();

            if (travTrocs.length > 0) {
              settravTrocsList(travTrocs);
            }
            else {
              setIsShowNoPartnerFound(true);
            }

            break;
        }
      } catch (error) {


        setLoading(false); //stop spinner

        if (error instanceof Error && error.message === "On ne connait pas le mesage de la reponse") {

          //redirect to home page
          location.href = "/";
        }
        else if (error instanceof Error && error.message === "erreur lors de la récupération des troc des résidents") {
          setRegion(null);
          setLoading(false);
          setgpsLoadingError(null);
          setRTrocsList([]);
          setIsShowNoPartnerFound(false);
        }
        else if (error instanceof Error && error.message === "On n'a pû trouver votre position") {
          setRegion(null);
          setLoading(false);
          setgpsLoadingError(false);
          setRTrocsList([]);
          setIsShowNoPartnerFound(false);
        }
        else if (error instanceof Error && error.message === "On n'a pû récupérer vos coord gps") {
          setRegion(null);
          setLoading(false);
          setgpsLoadingError(true);
          setRTrocsList([]);
          setIsShowNoPartnerFound(false);
        }
        else {
          setRegion(null);
          setLoading(false);
          setgpsLoadingError(false);
          setRTrocsList([]);
          setIsShowNoPartnerFound(false);
        }
      }
      finally {
        setLoading(false); //stop spinner
      }
    }
    getTrocsUsers();
  }, [triggerUseEffect]) //no dependencies cause useEffect have to trigger to each render to get user GPS data

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      let listener: PluginListenerHandle;

      const setupListener = async () => {
        listener = await App.addListener('appStateChange', async (state) => {
          if (state.isActive && isWaitingForLocationActivation.current) {
            isWaitingForLocationActivation.current = false;
            settriggerUseEffect((prev) => !prev);
          }
        });
      };

      setupListener();

      return () => {
        if (listener) {
          listener.remove(); // suppression du listener à la destruction
        }
      };
    }
  }, []);

  switch (userRole) {
    case "TRAVELER":
      if (region && rTrocsList.length > 0) {

        return (
          <section id="section-ask-trocs" className=" flex flex-col gap-10 pb-20">

            <h1 className="text-greenCustom font-bold">Trocs des résidents</h1>

            <SearchTrocsTable trocsResidents={rTrocsList} trocsTravelers={[]} triggerUseEffect={triggerUseEffect} settriggerUseEffect={settriggerUseEffect} setRTrocsList={setRTrocsList} />

          </section>
        )

      }
      else if (loading) {

        return <SpinnerC />

      }
      else if (gpsLoadingError === false) {

        return <p>Nous n'avons pas pû déterminer votre région actuelle. Veuillez vérifier vos paramètres de localisation</p>

      }
      else if (gpsLoadingError) {

        return (
          <>
            <p>Nous n'avons pas pû déterminer votre position. Veuillez vérifier dans vos paramètres de localisation</p>

            {
              Capacitor.getPlatform() === "android" ?
                (
                  <GpsToast
                    visible={showToastGpsSetting}
                    onClose={() => setShowToastGpsSetting(false)}
                    onConfirm={handleConfirmOpenSettings}
                  />
                ) :
                null
            }
          </>
        )
      }
      else if (isShowNoPartnerFound) {

        return <NoPartnerFound />

      }
      else {

        return <ErrorOccurred />;

      }
    case "RESIDENT":
      if (travTrocsList) {
        return (
          <section id="section-ask-trocs" className=" flex flex-col gap-10 pb-20">

            <h1 className="text-greenCustom font-bold">Trocs des voyageurs</h1>

            <SearchTrocsTable trocsResidents={[]} trocsTravelers={travTrocsList} triggerUseEffect={triggerUseEffect} settriggerUseEffect={settriggerUseEffect} />

          </section>
        )
      }
      else if (loading) {
        return <SpinnerC />
      }
      else if (isShowNoPartnerFound) {
        return <NoPartnerFound />
      }
      else {
        return <ErrorOccurred />;
      }
    default: return <ErrorOccurred />;
  }

}
export default SearchTrocs;

function NoPartnerFound() {
  //get user role via redux
  const userRole = useSelector((state: RootState) => state.user.role);

  switch (userRole) {
    case "TRAVELER":
      return (
        <div className="h-full flex items-center justify-center">
          <p>Aucun troc dans cette région pour le moment</p>
        </div>
      )
    case "RESIDENT":
      return (
        <div className="h-full flex items-center justify-center">
          <p>Aucun voyageur de disponible pour le moment !</p>
        </div>
      )
  }
}